import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Col, DatePicker, Form, Input, Row, Select, Spin, Tooltip, message } from "antd";
import { FormInstance } from "antd/es/form/Form";
import moment, { Moment } from "moment";
import NumberFormat from "react-number-format";
import { round } from "lodash";
import {
  checkIsLoading,
  displayFormat,
  getDictionaryItemById,
  getSelectOptions,
  toDisplayFormat,
  toHttpFormatStartOfDay,
  getVersionsDateRange,
} from "../../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../..";
import useDebounce from "../../../lib/hooks/useDebounce";
import { Offer } from "../../../lib/types";
import { fetchTax, taxTsActions } from "../../../store/slices/taxTs";
import { rangeFromLimitValidator } from "../../common/ServiceRate/utils/rangeFromLimitValidator";
import { rangeToLimitValidator } from "../../common/ServiceRate/utils/rangeToLimitValidator";
import { fetchDefaultServiceNomenclature } from "../../../store/slices/offering/offering";
import { IViewModeExtended } from "../../../lib/types/IViewMode";
import { OFFERING_PRICE_NEGATIVEAMOUNT } from "../../../lib/const/permissions";
import { hasAccess } from "../../RolesModule/hasAccess";
import { AggregationRule } from "../../../lib/types/AggregationRule";
import { RateModes } from "../../../lib/const/rateModes";
import { getAllowedRateTypes } from "../../common/ServiceRate/utils/getAllowedRateTypes";
import { getPriceUnitId } from "../../../containers/OfferContainer/utils/getPriceUnitId";
import { TariffModelTypes } from "../../../lib/const/tariffModelTypes";
import { getRateTypeId } from "../../../containers/OfferContainer/utils/getRateTypeId";
import { getAllowedPriceUnits } from "../../common/ServiceRate/utils/getAllowedPriceUnits";
import { PriceUnits } from "../../../lib/const/priceUnits";
import { RateTypes } from "../../../lib/const/rateTypes";
import { FormInfo } from "../../common/FormInfo/FormInfo";
import {
  aggregationRulesActions,
  fetchAggregationRuleVersionsNotForViewing,
} from "../../../modules/AggregationRules/store/slice/aggregationRulesSlice";
import "./OfferPriceParamsEdit.less";

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
  className: "form-item-required__hidden-error__mb0",
};

const rowLayout = {
  gutter: 50,
  style: {
    marginTop: "10px",
  },
};

interface ComponentProps {
  isChild?: boolean;
  isLoading?: boolean;
  offerPriceParamsForm: FormInstance<any>;
  offer?: Offer;
  viewMode: IViewModeExtended;
  onChangeFromDateCharacteristic?: (fromDate?: moment.Moment | null) => void;
  aggregationRules: AggregationRule<"view">[];
}

export const OfferPriceParamsEdit = ({
  isChild = false,
  isLoading = false,
  offerPriceParamsForm,
  offer,
  viewMode,
  onChangeFromDateCharacteristic,
  aggregationRules,
}: ComponentProps) => {
  const {
    rateType,
    rateTypeLoading,
    dicCurrencies,
    dicCurrenciesLoading,
    priceEvents,
    priceEventsLoading,
    priceUnits,
    priceUnitsLoading,
    chargeNames,
    chargeNamesLoading,
    rateModesEvents,
    rateModesEventsLoading,
    conditionTypes: conditionTypesDic,
    conditionTypesLoading: conditionTypesDicLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const { tax, taxLoading } = useAppSelector((state) => state.taxTs);
  const { tenantProperties } = useAppSelector((state) => state.tenants);
  const { offerVersionsNotForViewing, offerPrice } = useAppSelector((state) => state.offering);
  const { aggregationRuleVersionsNotForViewing } = useAppSelector(
    (state) => state.aggregationRules
  );
  const dispatch = useAppDispatch();

  const availableDates = useMemo(() => {
    let fromDate: Moment | undefined;
    let toDate: Moment | undefined;
    switch (viewMode) {
      case "add":
        fromDate = offerPrice?.fromDate ? moment(offerPrice.fromDate).add(1, "day") : undefined;
        toDate = offerPrice?.toDate ? moment(offerPrice.toDate) : undefined;
        break;
      case "close":
        toDate = offerPrice?.toDate ? moment(offerPrice.toDate) : undefined;
        break;
      case "create":
        const dateRange = getVersionsDateRange(offerVersionsNotForViewing);
        fromDate = dateRange.fromDate ? moment(dateRange.fromDate) : undefined;
        toDate = dateRange.toDate ? moment(dateRange.toDate) : undefined;
        break;
      default:
        break;
    }
    return { fromDate, toDate };
  }, [viewMode, offerVersionsNotForViewing, offerPrice]);

  // Для использования в дизейбле дат, для вида "Трафик", нужны версии выбранного правила
  const onSelectAggregationRuleId = (aggregationRuleId: number) => {
    offerPriceParamsForm.setFieldsValue({ aggregationRuleId });
    dispatch(fetchAggregationRuleVersionsNotForViewing(aggregationRuleId));
  };

  // Очистка aggregationRuleVersionsNotForViewing
  useEffect(
    () => () => {
      dispatch(aggregationRulesActions.cleanAggregationRuleVersionsNotForViewing());
    },
    []
  );

  const disableFromDate = useCallback(
    (params: { date: Moment; rateModeId?: RateModes; toDate?: Moment }): boolean => {
      const { date, rateModeId, toDate } = params;
      if (viewMode === "create" && rateModeId === RateModes.TRAFFIC) {
        const dateRange = getVersionsDateRange(aggregationRuleVersionsNotForViewing);
        if (dateRange.fromDate && dateRange.toDate) {
          const toDateBorder = toDate || dateRange.toDate;
          return !date.isBetween(dateRange.fromDate, toDateBorder, "day", "[]");
        }
      }
      const toDateBorder = toDate || availableDates?.toDate;
      if (availableDates?.fromDate && toDateBorder) {
        return !date.isBetween(availableDates.fromDate, toDateBorder, "day", "[]");
      }
      return false;
    },
    [aggregationRuleVersionsNotForViewing, availableDates]
  );

  const disableToDate = useCallback(
    (params: { date: Moment; rateModeId?: RateModes; fromDate?: Moment }): boolean => {
      const { date, rateModeId, fromDate } = params;
      if (viewMode === "create" && rateModeId === RateModes.TRAFFIC) {
        const dateRange = getVersionsDateRange(aggregationRuleVersionsNotForViewing);
        if (dateRange.fromDate && dateRange.toDate) {
          const fromDateBorder = fromDate || dateRange.fromDate;
          return !date.isBetween(fromDateBorder, dateRange.toDate, "day", "[]");
        }
      }
      const fromDateBorder = fromDate || availableDates?.fromDate;
      if (fromDateBorder && availableDates?.toDate) {
        return !date.isBetween(fromDateBorder, availableDates.toDate, "day", "[]");
      }
      return false;
    },
    [aggregationRuleVersionsNotForViewing, availableDates]
  );

  const [selectedFromDate, setSelectedFromDate] = useState<Moment | null>(moment());

  // Обновление ставки налога в serviceRate, при измененении: fromDate, serviceTypeId
  const updateTaxRate = useDebounce(() => {
    const serviceTypeId = offer?.serviceTypeId;

    if (serviceTypeId && selectedFromDate) {
      dispatch(
        fetchTax({
          serviceTypeId,
          amount: 1, // Нужна только ставка налога, поэтому передается 1
          amountType: "NET",
          onDate: toHttpFormatStartOfDay(selectedFromDate),
        })
      );
    } else {
      dispatch(taxTsActions.cleanTax());
    }
  }, 300);

  // Обновление ставки налога в serviceRate, при измененении fromDate
  useEffect(() => {
    updateTaxRate();
  }, [selectedFromDate, offer?.serviceTypeId]);

  // В России редактируется цена без налога, иначе - с. Флаг об этом
  const isEditPriceWithVat = useMemo(() => (tenantProperties as any)?.inputTax === "true", [
    tenantProperties,
  ]);

  const onChangePrice = () => {
    const priceValueForm = offerPriceParamsForm.getFieldValue("price");
    const priceWithVatValueForm = offerPriceParamsForm.getFieldValue("priceWithVat");
    const taxRate = tax?.taxRate || "0";
    const taxMultiplier = Number(taxRate) / 100 + 1;

    const price = isEditPriceWithVat ? `${priceWithVatValueForm / taxMultiplier}` : priceValueForm;
    const priceWithVat = isEditPriceWithVat
      ? priceWithVatValueForm
      : `${priceValueForm * taxMultiplier}`;

    offerPriceParamsForm.setFieldsValue({
      priceWithVat,
      price,
    });
  };

  const getPriceWithVat = () => {
    const price = Number(offerPrice?.price) || 0;
    const taxRate = tax?.taxRate || "0";
    const taxMultiplier = Number(taxRate) / 100 + 1;

    return round(price * taxMultiplier, 2).toFixed(2);
  };

  // Обновление цен и ставки налога, при изменении налога
  useEffect(() => {
    onChangePrice();
  }, [tax, isEditPriceWithVat]);

  const onChangeRateModeId = (value: RateModes) => {
    const values: {
      priceUnitId?: PriceUnits;
      rateTypeId?: RateTypes;
      rangeFrom: "";
      rangeTo: "";
      fromDate?: Moment;
      toDate?: Moment;
      aggregationRuleId?: number;
    } = {
      priceUnitId: getPriceUnitId(offer?.tariffModelId === TariffModelTypes.PACKET, value),
      rateTypeId: getRateTypeId(value),
      rangeTo: "",
      rangeFrom: "",
    };
    if (value === RateModes.TRAFFIC) {
      values.fromDate = undefined;
      values.toDate = undefined;
    } else {
      values.aggregationRuleId = undefined;
    }

    offerPriceParamsForm.setFieldsValue(values);
  };

  const loadDefaultNomenclature = (serviceTypeId) => {
    dispatch(fetchDefaultServiceNomenclature(serviceTypeId));
  };

  useEffect(() => {
    loadDefaultNomenclature(7064);
  }, []);

  const renderDataItem = (props: {
    label: string | JSX.Element;
    data?: ReactNode;
    offsetCol?: number;
    spanCol?: number;
    labelCol?: number;
    wrapperCol?: number;
  }): ReactNode => {
    const { label, data, offsetCol = 0, spanCol = 6, labelCol = 10, wrapperCol = 14 } = props;
    return (
      <Col offset={offsetCol} span={spanCol}>
        <Row className="offer-price-params-edit__dataRow ant-row ant-form-item form-item-required__hidden-error__mb0">
          <Col span={labelCol} className="ant-form-item-label ant-form-item-label-left">
            <label>{label}</label>
          </Col>
          <Col
            span={wrapperCol}
            className="offer-price-params-edit__dataRow__dataCol ant-form-item-control"
          >
            <span>{data}</span>
          </Col>
        </Row>
      </Col>
    );
  };

  const childClassName = useMemo(() => {
    if (!isChild) return "";
    // if (isCurrentVersion) return `row_child row_current-version`;
    // if (isPastVersion) return "row_child row_past-version";
    return "row_child offer-price-params-edit__price_child";
  }, [offer, isChild]);

  const getAggregationRuleTooltip = (ruleId: number | undefined) => {
    if (!ruleId) {
      return null;
    }
    const selectedAggregationRule = aggregationRules.find(({ id }) => id === ruleId);
    if (selectedAggregationRule) {
      const { aggName, rangeFrom, rangeTo } = selectedAggregationRule;
      return (
        <>
          <span>{aggName}</span>
          <span>от: {rangeFrom}</span>
          <span>до: {rangeTo}</span>
        </>
      );
    }
    return null;
  };

  return (
    <div className={`offer-price-params-edit ${childClassName}`}>
      <Spin spinning={isLoading}>
        <Row {...rowLayout}>
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item
                label="Наименование"
                name="name"
                rules={[{ required: true, message: "" }]}
                {...formItemLayout}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Наименование",
                data: offerPrice?.name,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item noStyle dependencies={["rateModeId"]}>
                {({ getFieldsValue }) => {
                  const { rateModeId } = getFieldsValue();
                  const allowedRateTypes = getAllowedRateTypes(rateType, rateModeId);
                  return (
                    <Form.Item
                      label="Тип расчета"
                      name="rateTypeId"
                      rules={[{ required: true, message: "" }]}
                      {...formItemLayout}
                    >
                      <Select
                        options={getSelectOptions({
                          list: allowedRateTypes,
                        })}
                        loading={checkIsLoading(rateTypeLoading)}
                        disabled={allowedRateTypes?.length <= 1}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Тип расчета",
                data: getDictionaryItemById(rateType, offerPrice?.rateTypeId)?.name,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item
                label="Событие"
                name="priceEventId"
                {...formItemLayout}
                rules={[{ required: true, message: "" }]}
              >
                <Select
                  loading={checkIsLoading(priceEventsLoading)}
                  options={getSelectOptions({
                    list: priceEvents,
                  })}
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Событие",
                data: getDictionaryItemById(priceEvents, offerPrice?.priceEventId)?.name,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item noStyle dependencies={["rateModeId"]}>
                {({ getFieldsValue }) => {
                  const { rateModeId } = getFieldsValue();
                  const allowedPriceUnits = getAllowedPriceUnits(
                    priceUnits,
                    offer?.tariffModelId === TariffModelTypes.PACKET,
                    rateModeId
                  );
                  return (
                    <Form.Item
                      label="Единица тарифика&shy;ции"
                      name="priceUnitId"
                      {...formItemLayout}
                      rules={[{ required: true, message: "" }]}
                    >
                      <Select
                        loading={checkIsLoading(priceUnitsLoading)}
                        options={getSelectOptions({
                          list: allowedPriceUnits,
                        })}
                        disabled={allowedPriceUnits?.length <= 1}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: <>Единица тарифика&shy;ции</>,
                data: getDictionaryItemById(priceUnits, offerPrice?.priceUnitId)?.name,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "add" || viewMode === "create" || viewMode === "edit" ? (
            <Col span={4}>
              <Form.Item
                className="form-item-dependencies"
                dependencies={["aggregationRuleId", "rateModeId", "toDate"]}
              >
                {({ getFieldValue }) => {
                  const aggregationRuleId = getFieldValue("aggregationRuleId");
                  const rateModeId = getFieldValue("rateModeId");
                  const toDate = getFieldValue("toDate");
                  const disabledByTrafficMode =
                    viewMode === "create" && rateModeId === RateModes.TRAFFIC && !aggregationRuleId;
                  return (
                    <Form.Item
                      label="Период с"
                      name="fromDate"
                      {...formItemLayout}
                      rules={
                        viewMode === "add" || viewMode === "create"
                          ? [{ required: true, message: "" }]
                          : undefined
                      }
                    >
                      {disabledByTrafficMode ? (
                        <Tooltip
                          title={
                            disabledByTrafficMode ? "Необходимо выбрать группу трафика" : undefined
                          }
                        >
                          <DatePicker disabled />
                        </Tooltip>
                      ) : (
                        <DatePicker
                          format={displayFormat}
                          disabledDate={(date) =>
                            disableFromDate({
                              date,
                              rateModeId,
                              toDate,
                            })
                          }
                          disabled={viewMode === "edit" || disabledByTrafficMode}
                          style={{ width: "100%" }}
                          onChange={(fromDate) => {
                            setSelectedFromDate(fromDate);
                            onChangeFromDateCharacteristic &&
                              onChangeFromDateCharacteristic(fromDate);
                          }}
                        />
                      )}
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Период с",
                data: offerPrice?.fromDate && toDisplayFormat(offerPrice.fromDate),
                spanCol: 4,
              })}
            </>
          )}
        </Row>
        <Row {...rowLayout}>
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item
                label="Вид"
                name="rateModeId"
                {...formItemLayout}
                rules={[{ required: true, message: "" }]}
              >
                <Select
                  loading={checkIsLoading(rateModesEventsLoading)}
                  options={getSelectOptions({
                    list: rateModesEvents.filter(({ id }) =>
                      aggregationRules.length ? true : id !== RateModes.TRAFFIC
                    ),
                  })}
                  onChange={onChangeRateModeId}
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Вид",
                data: getDictionaryItemById(rateModesEvents, offerPrice?.rateModeId)?.name,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Tooltip title={tax ? "" : "Для расчета налога задайте дату создания"}>
                <Form.Item
                  label={
                    <Row>
                      <Spin spinning={checkIsLoading(taxLoading)}>
                        {`Цена с налогом (${tax?.taxRate ? round(Number(tax.taxRate), 2) : ""}%)`}
                      </Spin>
                    </Row>
                  }
                  name="priceWithVat"
                  normalize={(value) => value.replace(/ /g, "")}
                  {...formItemLayout}
                  rules={[{ required: isEditPriceWithVat, message: "" }]}
                >
                  <NumberFormat
                    thousandSeparator={" "}
                    className="ant-input-affix-wrapper inputNumber__hideArrows"
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={hasAccess(OFFERING_PRICE_NEGATIVEAMOUNT)}
                    disabled={!isEditPriceWithVat}
                    onChange={onChangePrice}
                    onValueChange={onChangePrice}
                  />
                </Form.Item>
              </Tooltip>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: (
                  <Row>
                    <Spin spinning={checkIsLoading(taxLoading)}>
                      {`Цена с налогом (${
                        offerPrice?.taxRate ? round(Number(offerPrice?.taxRate), 2) : ""
                      }%)`}
                    </Spin>
                  </Row>
                ),
                data: getPriceWithVat(),
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item
                label="Валюта"
                name="currencyId"
                rules={[{ required: true, message: "" }]}
                {...formItemLayout}
              >
                <Select
                  options={getSelectOptions({
                    list: dicCurrencies,
                  })}
                  allowClear
                  loading={checkIsLoading(dicCurrenciesLoading)}
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Валюта",
                data: getDictionaryItemById(dicCurrencies, offerPrice?.currencyId)?.name,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item noStyle dependencies={["rangeTo", "rateModeId"]}>
                {({ getFieldsValue, validateFields }) => {
                  const { rangeTo, rateModeId } = getFieldsValue();
                  return (
                    <Form.Item
                      label="От (шт)"
                      name="rangeFrom"
                      {...formItemLayout}
                      normalize={(value) => value.replace(/ /g, "")}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (
                              rangeFromLimitValidator({
                                rangeFrom: value,
                                limitTo: rangeTo,
                              })
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject();
                          },
                        },
                      ]}
                    >
                      <NumberFormat
                        thousandSeparator={" "}
                        className="ant-input-affix-wrapper inputNumber__hideArrows"
                        decimalScale={0}
                        fixedDecimalScale
                        onBlur={(event) => {
                          const rangeFrom = event.target.value.replace(/ /g, "");
                          const isValueValid = rangeFromLimitValidator({
                            rangeFrom,
                            limitTo: rangeTo,
                          });

                          validateFields(["rangeTo"]);

                          if (!isValueValid)
                            message.warning('Значение "От" не должно быть больше значения "До"');
                        }}
                        allowNegative={false}
                        disabled={!rateModeId || rateModeId !== 3}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "От (шт)",
                data: offerPrice?.rangeFrom,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "add" ||
          viewMode === "create" ||
          viewMode === "close" ||
          viewMode === "edit" ? (
            <Col span={4}>
              <Form.Item
                className="form-item-dependencies"
                dependencies={["aggregationRuleId", "rateModeId", "fromDate"]}
              >
                {({ getFieldValue }) => {
                  const aggregationRuleId = getFieldValue("aggregationRuleId");
                  const rateModeId = getFieldValue("rateModeId");
                  const fromDate = getFieldValue("fromDate");
                  const disabledByTrafficMode =
                    viewMode === "create" && rateModeId === RateModes.TRAFFIC && !aggregationRuleId;
                  return (
                    <Form.Item
                      label="Период по"
                      name="toDate"
                      {...formItemLayout}
                      rules={viewMode === "close" ? [{ required: true, message: "" }] : undefined}
                    >
                      {disabledByTrafficMode ? (
                        <Tooltip
                          title={
                            disabledByTrafficMode ? "Необходимо выбрать группу трафика" : undefined
                          }
                        >
                          <DatePicker disabled />
                        </Tooltip>
                      ) : (
                        <DatePicker
                          format={displayFormat}
                          disabledDate={(date) =>
                            disableToDate({
                              date,
                              fromDate,
                              rateModeId,
                            })
                          }
                          style={{ width: "100%" }}
                          disabled={["edit", "add"].includes(viewMode) || disabledByTrafficMode}
                        />
                      )}
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Период по",
                data: offerPrice?.toDate && toDisplayFormat(offerPrice.toDate),
                spanCol: 4,
              })}
            </>
          )}
        </Row>
        <Row {...rowLayout}>
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item noStyle dependencies={["rateModeId", "aggregationRuleId"]}>
                {({ getFieldsValue }) => {
                  const { rateModeId, aggregationRuleId } = getFieldsValue();
                  return (
                    <Form.Item
                      label="Группа трафика"
                      name="aggregationRuleId"
                      rules={[{ required: rateModeId === RateModes.TRAFFIC, message: "" }]}
                      {...formItemLayout}
                    >
                      <Tooltip
                        title={getAggregationRuleTooltip(aggregationRuleId)}
                        overlayClassName="offer-price-params-edit__aggregationRuleData"
                        placement="rightBottom"
                      >
                        <Select
                          disabled={rateModeId !== RateModes.TRAFFIC}
                          options={getSelectOptions({
                            list: aggregationRules.map((rule) => ({
                              ...rule,
                              aggName: (
                                <Tooltip
                                  title={getAggregationRuleTooltip(rule.id)}
                                  overlayClassName="offer-price-params-edit__aggregationRuleData"
                                  placement="rightBottom"
                                >
                                  <div>{rule.aggName}</div>
                                </Tooltip>
                              ),
                            })),
                            labelName: "aggName",
                          })}
                          onSelect={onSelectAggregationRuleId}
                          value={aggregationRuleId}
                          className="offer-price-params-edit__hideTooltip"
                        />
                      </Tooltip>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Группа трафика",
                data: getDictionaryItemById(aggregationRules, offerPrice?.aggregationRuleId)
                  ?.aggName,
                spanCol: 5,
              })}
            </>
          )}

          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Tooltip title={tax ? "" : "Для расчета налога задайте дату создания"}>
                <Form.Item
                  label="Цена без налога"
                  name="price"
                  normalize={(value) => value.replace(/ /g, "")}
                  {...formItemLayout}
                  rules={[{ required: !isEditPriceWithVat, message: "" }]}
                >
                  <NumberFormat
                    thousandSeparator={" "}
                    className="ant-input-affix-wrapper inputNumber__hideArrows"
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={hasAccess(OFFERING_PRICE_NEGATIVEAMOUNT)}
                    disabled={isEditPriceWithVat}
                    onChange={onChangePrice}
                    onValueChange={onChangePrice}
                  />
                </Form.Item>
              </Tooltip>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Цена без налога",
                data: Number(offerPrice?.price || 0).toFixed(2),
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item
                label="Условие расчета"
                name="conditionTypes"
                {...formItemLayout}
                rules={[{ required: true, message: "" }]}
              >
                <Select
                  loading={checkIsLoading(conditionTypesDicLoading)}
                  options={getSelectOptions({
                    list: conditionTypesDic,
                  })}
                  mode="multiple"
                  maxTagCount="responsive"
                  allowClear
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Условие расчета",
                data: offerPrice?.conditionTypes?.length
                  ? offerPrice.conditionTypes
                      .map((typeId) => getDictionaryItemById(conditionTypesDic, typeId)?.name)
                      .filter(Boolean)
                      .join(", ")
                  : getDictionaryItemById(conditionTypesDic, 0)?.name,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item noStyle dependencies={["rangeFrom", "rateModeId", "rangeTo"]}>
                {({ getFieldsValue, validateFields }) => {
                  const { rangeFrom, rateModeId } = getFieldsValue();

                  return (
                    <Form.Item
                      label="До (шт)"
                      name="rangeTo"
                      {...formItemLayout}
                      normalize={(value) => value.replace(/ /g, "")}
                      rules={[
                        {
                          validator: (_, rangeTo) => {
                            if (
                              rangeToLimitValidator({
                                rangeTo,
                                limitFrom: rangeFrom,
                              })
                            ) {
                              return Promise.resolve();
                            }
                            return Promise.reject();
                          },
                        },
                      ]}
                    >
                      <NumberFormat
                        thousandSeparator={" "}
                        className="ant-input-affix-wrapper inputNumber__hideArrows"
                        decimalScale={0}
                        fixedDecimalScale
                        onBlur={(event) => {
                          const rangeTo = event.target.value.replace(/ /g, "");
                          const isValueValid = rangeToLimitValidator({
                            limitFrom: rangeFrom,
                            rangeTo,
                          });

                          validateFields(["rangeFrom"]);

                          if (!isValueValid)
                            message.warning('Значение "До" не должно быть меньше значения "От"');
                        }}
                        allowNegative={false}
                        disabled={!rateModeId || rateModeId !== 3}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "До (шт)",
                data: offerPrice?.rangeTo,
                spanCol: 5,
              })}
            </>
          )}
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={4}>
              <Form.Item
                label="Номен&shy;кла&shy;ту&shy;ра"
                name="chargeNameId"
                {...formItemLayout}
                rules={[{ required: true, message: "" }]}
              >
                <Select
                  loading={checkIsLoading(chargeNamesLoading)}
                  options={getSelectOptions({
                    list: chargeNames,
                  })}
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: <>Номен&shy;кла&shy;ту&shy;ра</>,
                data: (
                  <Tooltip
                    title={getDictionaryItemById(chargeNames, offerPrice?.chargeNameId)?.name}
                  >
                    <div
                      style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
                    >
                      {getDictionaryItemById(chargeNames, offerPrice?.chargeNameId)?.name}
                    </div>
                  </Tooltip>
                ),
                spanCol: 4,
              })}
            </>
          )}
        </Row>
        <Row {...rowLayout}>
          {viewMode === "edit" || viewMode === "add" || viewMode === "create" ? (
            <Col span={5}>
              <Form.Item
                label="Тип баланса"
                rules={[{ required: true, message: "" }]}
                {...formItemLayout}
              >
                <Input value="Денежный" disabled />
              </Form.Item>
            </Col>
          ) : (
            <>
              {renderDataItem({
                label: "Тип баланса",
                data: "Денежный",
                spanCol: 5,
              })}
            </>
          )}
        </Row>
      </Spin>
      <FormInfo
        getFormValues={offerPriceParamsForm.getFieldsValue}
        className="offer-price-params-edit__formInfo"
      />
    </div>
  );
};
